import styled from "styled-components";
import { SpreadWhiteAnim, SpreadBlackAnim, fadeIn } from "./animation";
import { colors, fonts } from "./theme";

const QUERIES = {
  large: `min-width: 1200px`,
  medium: `min-width: 870px`,
  small: `min-width: 660px`,
  maxWidth: `1500px`,
};

// Copper emphasis inside display headings (upright: italics are harder for dyslexic readers): <Heading2>Read Faster, <em>Retain Longer</em></Heading2>
const displayEmphasis = `
  em {
    font-style: normal;
    color: ${colors.copper};
  }
`;

export const Heading = styled.h1`
  font-family: ${fonts.display};
  font-size: 50px;
  line-height: 1.05;
  font-weight: 300;
  margin: 0 auto;
  width: 100%;
  color: ${colors.ink};
  transition: all 0.3s ease-in-out;
  user-select: none;
  opacity: 0;
  letter-spacing: -0.02em;
  max-width: 700px;
  text-align: center;
  animation: ${fadeIn} 1s forwards;
  ${displayEmphasis}

  @media (${QUERIES.large}) {
    font-size: 82px;
  }
`;

// Stat statements. $accent tints the short rule above the line.
export const BulletHeading = styled.h2`
  font-family: ${fonts.display};
  font-size: 34px;
  line-height: 1.15;
  font-weight: 300;
  color: ${colors.ink};
  opacity: 0;
  letter-spacing: -0.015em;
  text-align: center;
  max-width: 920px;
  margin: 0 auto;
  animation: ${fadeIn} 1s forwards;
  text-wrap: balance;

  &::before {
    content: "";
    display: block;
    width: 56px;
    height: 3px;
    border-radius: 3px;
    margin: 0 auto 28px;
    background: ${(props) => props.$accent || colors.copper};
  }

  @media (${QUERIES.medium}) {
    font-size: 46px;
  }

  @media (${QUERIES.large}) {
    font-size: 56px;
  }
`;

export const Heading2 = styled.h1`
  font-family: ${fonts.display};
  font-size: 48px;
  line-height: 1.02;
  font-weight: 300;
  color: ${colors.ink};
  opacity: 0;
  letter-spacing: -0.025em;
  animation: ${fadeIn} 1s forwards;
  ${displayEmphasis}

  @media (${QUERIES.medium}) {
    font-size: 72px;
  }

  @media (${QUERIES.large}) {
    font-size: 96px;
  }
`;

export const Heading3 = styled.h3`
  font-family: ${fonts.display};
  font-size: 32px;
  line-height: 1.1;
  font-weight: 400;
  color: ${colors.ink};
  opacity: 0;
  letter-spacing: -0.015em;
  animation: ${fadeIn} 1s forwards;
  ${displayEmphasis}

  @media (${QUERIES.large}) {
    font-size: 44px;
  }
`;

export const Paragraph = styled.p`
  font-family: ${fonts.body};
  font-weight: 400;
  line-height: 1.6;
  font-size: 18px;
  color: ${colors.bark};

  @media (${QUERIES.large}) {
    font-size: 21px;
  }
`;

export const Paragraph2 = styled.p`
  font-family: ${fonts.body};
  font-weight: 400;
  line-height: 1.6;
  font-size: 18px;
  color: ${colors.bark};
  margin: 16px 0;

  @media (${QUERIES.large}) {
    font-size: 21px;
    margin: 24px 0;
  }
`;

export const Caption = styled.p`
  color: ${colors.pebble};
  font-family: ${fonts.body};
  font-weight: 400;
  line-height: 1.5;
  font-size: 15px;

  @media (${QUERIES.large}) {
    font-size: 16px;
    margin: 10px 0;
  }
`;

export const Main = styled.main`
  width: 100%;
  height: 100%;
  background-color: ${colors.limestone};
  /* clip (not hidden) so position: sticky keeps working inside */
  overflow-x: hidden;
  overflow-x: clip;
  @media (${QUERIES.large}) {
    padding-right: ${(props) => props.paddingRight};
  }

  > .growBlack {
    animation: ${SpreadBlackAnim} 2s forwards;
  }

  > .growWhite {
    animation: ${SpreadWhiteAnim} 2s forwards;
  }
`;

export const Full = styled.main`
  width: 100%;
  height: 100%;
  padding: 0px;
  max-width: 1500px;
  margin: 50px auto;

  @media (${QUERIES.large}) {
    padding: 20px;
    margin: 50px auto;
  }
`;

export const Section = styled.div`
  width: 100%;
  height: auto;
  margin: 20px auto 50px auto;
  padding: 30px;
  max-width: 1110px;

  @media (${QUERIES.large}) {
    margin: 100px auto 170px auto;
    padding: 0px;
  }
`;

export const Inner = styled.div`
  max-width: 1437px;
  width: 100%;
  height: auto;
  margin: 0 auto;
  padding: 30px;

  display: flex;
  flex-direction: column;

  > * {
    width: 100%;
  }
`;

export const Image = styled.div`
  width: 300px;
  height: 300px;
  background-image: url(${(props) => props.mobileImage});
  background-position: center center;
  background-size: contain;
  background-repeat: no-repeat;
  opacity: 0;
  animation: ${fadeIn} 1s forwards;
  z-index: -1;
  margin: 0 auto;

  @media (${QUERIES.medium}) {
    width: 100%;
    height: 300px;
    background-image: url(${(props) => props.image});
    margin: 0 auto;
    padding: 0px;
  }
  @media (${QUERIES.large}) {
    width: 100%;
    height: 380px;
    background-image: url(${(props) => props.image});
    margin: 0 auto;
    padding: 0px;
  }
`;

export const RowSpacer = styled.div`
  width: 100%;
  padding: 20px 0;
  @media (${QUERIES.large}) {
    padding: 80px 0;
  }
`;
export const RowSpacer2 = styled.div`
  width: 100%;
  padding: 5px 0;
  @media (${QUERIES.large}) {
    padding: 15px 0;
  }
`;
export default QUERIES;
